/**
 * IMPORT A FORM FROM A PDF — `tasks/patient-forms.md` §8, §16.
 *
 * Upload a PDF, wait for a vision call per page, and come back with a DRAFT that a person reviews.
 *
 * ── THE ONE RULE THIS DIALOG EXISTS TO PROTECT ───────────────────────────────────────────────
 *
 * §16.2 rule 1: **the extraction is a draft a human reviews and publishes, never something the
 * system publishes.** So there is no Publish button anywhere on this screen — not on the completion
 * step, not hidden behind a menu. The primary action when an extraction finishes is *review*, the
 * completion copy says in its own heading that nothing has been published, and the only thing this
 * dialog can hand back to its caller is a schema for the builder to open. A screen that said "your
 * form is ready" would be an invitation to send a questionnaire nobody has read to a patient, and a
 * 10-page scan is exactly where an extraction drops a page.
 *
 * ── WHY THE WAIT IS A FIRST-CLASS STATE RATHER THAN A SPINNER ────────────────────────────────
 *
 * One vision call per page is tens of seconds, and §16.1 gave the extraction a home
 * (`CustomFormImports`) precisely so it survives the tab closing. A modal that trapped the user on
 * a spinner would waste that: the waiting step says, in as many words, that they can close the
 * window and the draft will be waiting. Both channels §16.3 offers are used — the
 * `customFormImportCompleted` / `customFormImportFailed` socket events as the nudge, and a slow
 * poll of `GET /custom-form-imports/:importId` underneath, because a socket that drops mid-import
 * would otherwise leave this waiting forever. The events go to the GROUP room and cannot carry an
 * encrypted draft schema, so either channel only ever triggers the same read.
 *
 * ── WHY THE FILE IS CHECKED HERE AS WELL AS ON THE SERVER ────────────────────────────────────
 *
 * §16.2 rule 3 caps the pages because vision is billed per page. The server enforces that cap; this
 * dialog states it before a byte is uploaded and refuses what it can see is over it, so a 300-page
 * misfire costs a message rather than an upload and a refusal. The page count is read out of the
 * PDF bytes best-effort (`readPageCount`): a file whose page tree is inside a compressed object
 * stream reads as "unknown", and an unknown count is NEVER a refusal — the server stays the
 * authority, and this check only ever catches what it is sure about.
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, interval, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { SocketService } from '../../../../core/services/socket.service';
import {
  CustomFormDto, CustomFormImportDto, CustomFormService,
} from '../../../../core/services/custom-form.service';
import { FormSchema } from '../../../../shared/models/custom-form.model';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * The caps this screen states out loud
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * §16.2 rule 3, and the server's `DEFAULT_MAX_PAGES` in `utils/customFormImportPrompt.js`.
 *
 * The server owns the real ceiling and it is env-tunable there (`CUSTOM_FORM_IMPORT_MAX_PAGES`),
 * so these two numbers CAN drift and §16.3 publishes no configuration endpoint that would let the
 * client ask. Two things keep that from becoming a lie on the screen: this copy is never allowed to
 * be looser than the default (a file this screen accepts and the server then refuses has cost the
 * upload, which is the thing rule 3 is about), and a refusal that names its own cap replaces this
 * number for the rest of the dialog — see `applyServerCap`.
 */
export const MAX_IMPORT_PAGES = 25;

/** A separate, deterministic guard: a file too big to be a questionnaire is refused by size. */
export const MAX_IMPORT_FILE_BYTES = 20 * 1024 * 1024;

export interface CustomFormImportDialogData {
  form: CustomFormDto;
}

/**
 * What this dialog can hand back. There is deliberately no "published" outcome and no version in
 * this shape: reviewing is the only thing pressing the primary button does.
 */
export interface CustomFormImportDialogResult {
  action: 'review';
  importId: string;
  schema: FormSchema;
  sourceFileName: string;
  sourceFileUrl: string | null;
  pageCount: number | null;
  schemaErrors: string[];
}

type ImportPhase = 'choose' | 'uploading' | 'waiting' | 'completed' | 'failed';

@Component({
  selector: 'app-custom-form-import-dialog',
  templateUrl: './custom-form-import-dialog.component.html',
  styleUrls: ['./custom-form-import-dialog.component.scss'],
})
export class CustomFormImportDialogComponent extends BaseComponent implements OnInit, OnDestroy {

  /** How often the fallback poll asks, in case the socket never arrives. */
  private static readonly POLL_INTERVAL_MS = 5000;

  readonly maxPagesDefault = MAX_IMPORT_PAGES;
  readonly maxFileMb = Math.round(MAX_IMPORT_FILE_BYTES / (1024 * 1024));

  /** The cap the screen quotes. Replaced by the server's own number if a refusal names one. */
  maxPages = MAX_IMPORT_PAGES;

  phase: ImportPhase = 'choose';

  /** The chosen file, once it has passed every check this client can make. */
  file: File | null = null;
  /** Pages read out of the file, or null when the bytes did not say. */
  filePageCount: number | null = null;
  /** Why the chosen file was refused. Shown against the picker, never as a toast that scrolls away. */
  fileError: string | null = null;

  /** The job, from the moment the upload returns 201. */
  currentImport: CustomFormImportDto | null = null;
  importError: string | null = null;

  /** The extracted DRAFT. Held here only to hand to the builder. Nothing publishes it. */
  extractedSchema: FormSchema | null = null;
  /** §16.2 rule 2 — the server's `validateSchema` verdict on that draft. */
  schemaErrors: string[] = [];

  /**
   * True when the extraction came back as `failed` but KEPT its draft (§16.2 rule 2).
   *
   * That is a document that was read and then refused by `validateSchema` — the common outcome for
   * a real scan — and it is the case the whole rule exists for: the pages are paid for, and a
   * person repairing the draft in the builder is cheaper and better than a second extraction that
   * will make the same mistakes. It is still not a form, and it still cannot be published until it
   * is fixed, so it gets its own sentence rather than being dressed up as a success.
   */
  needsRepair = false;

  /** Seconds since the upload returned, so the wait reads as progress rather than as a hang. */
  elapsedSeconds = 0;

  private pollSub: Subscription | null = null;
  private tickSub: Subscription | null = null;
  private socketSubs: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CustomFormImportDialogData,
    private dialogRef: MatDialogRef<CustomFormImportDialogComponent, CustomFormImportDialogResult>,
    private customFormService: CustomFormService,
    private socketService: SocketService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.listenForImportEvents();
  }

  override ngOnDestroy(): void {
    this.stopWatching();
    for (const sub of this.socketSubs) sub.unsubscribe();
    super.ngOnDestroy();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Choosing a file
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  onFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const chosen = input && input.files && input.files.length > 0 ? input.files[0] : null;
    // The same file picked twice must re-run the checks, and `change` does not fire on an
    // unchanged value.
    if (input) input.value = '';
    if (chosen) void this.selectFile(chosen);
  }

  /**
   * Every check that can be made without spending a vision call.
   *
   * The order matters: name, then size, then the bytes themselves. A .pdf-named Word document
   * passes the first check and fails the magic-number one, which is the whole reason that check
   * exists — the extension is a claim, the header is evidence.
   */
  async selectFile(file: File): Promise<void> {
    this.file = null;
    this.filePageCount = null;
    this.fileError = null;

    const name = file.name || 'that file';

    if (!this.looksLikePdfByName(file)) {
      this.fileError = `Only a PDF can be imported, and "${name}" is not one. `
        + 'Scan or export the questionnaire as a PDF and try again.';
      return;
    }

    if (file.size > MAX_IMPORT_FILE_BYTES) {
      this.fileError = `"${name}" is ${this.megabytes(file.size)} MB, which is more than an import `
        + `accepts (${this.maxFileMb} MB) — scan at a lower resolution, or split the document.`;
      return;
    }

    let bytes: Uint8Array;
    try {
      bytes = new Uint8Array(await file.arrayBuffer());
    } catch {
      this.fileError = `"${name}" could not be read from this device. Try choosing it again.`;
      return;
    }

    if (!CustomFormImportDialogComponent.hasPdfHeader(bytes)) {
      this.fileError = `"${name}" is named like a PDF but is not one — its contents are something `
        + 'else. Open it, export it as a PDF, and try again.';
      return;
    }

    const pages = CustomFormImportDialogComponent.readPageCount(bytes);
    if (pages !== null && pages > this.maxPages) {
      // §16.2 rule 3: a refusal that NAMES the cap. "Too long" without the number is a dead end.
      this.fileError = `"${name}" has ${pages} pages. Each page is read separately, so an import `
        + `is capped at ${this.maxPages} pages — split the document and import the parts as `
        + 'separate forms.';
      return;
    }

    this.file = file;
    this.filePageCount = pages;
  }

  clearFile(): void {
    this.file = null;
    this.filePageCount = null;
    this.fileError = null;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Uploading, and then waiting
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  upload(): void {
    if (!this.file || this.phase === 'uploading') return;

    this.phase = 'uploading';
    this.importError = null;

    this.customFormService.startImport(this.data.form.id, this.file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.currentImport = response.data;
          this.phase = 'waiting';
          this.elapsedSeconds = 0;
          this.startWatching();
          // A job that finished before the first poll interval (a one-page document) must not sit
          // on a waiting screen for five seconds pretending otherwise.
          this.refreshImport();
        },
        error: (error) => {
          this.phase = 'choose';
          this.applyServerCap(error);
          this.fileError = this.messageFor(error,
            'The document could not be uploaded. Check your connection and try again.');
        },
      });
  }

  /**
   * Poll and socket together — either can finish the wait, neither is trusted alone.
   *
   * The poll is the slow one on purpose: it is the fallback for a dropped socket, not the primary
   * channel, and an extraction that takes a minute does not need to be asked about twice a second.
   */
  private startWatching(): void {
    this.stopWatching();

    this.pollSub = interval(CustomFormImportDialogComponent.POLL_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshImport());

    this.tickSub = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.elapsedSeconds++; });
  }

  private stopWatching(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.tickSub?.unsubscribe();
    this.tickSub = null;
  }

  /**
   * §16.3's socket events. They carry an id and a status to the group room — never the schema,
   * which is encrypted — so both of them do exactly one thing: trigger the read that does.
   */
  private listenForImportEvents(): void {
    const wake = (event: any) => {
      if (!event || !this.currentImport) return;
      const id = event.id || event.importId;
      if (id && String(id) !== String(this.currentImport.id)) return;
      this.refreshImport();
    };

    this.socketSubs.push(
      this.socketService.listen('customFormImportCompleted', 'custom-form-import-dialog')
        .subscribe(wake));
    this.socketSubs.push(
      this.socketService.listen('customFormImportFailed', 'custom-form-import-dialog')
        .subscribe(wake));
  }

  private refreshImport(): void {
    if (!this.currentImport) return;
    if (this.phase === 'completed' || this.phase === 'failed') return;

    this.customFormService.getImport(this.currentImport.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.applyImport(response.data),
        error: () => {
          // A single failed poll is not a failed import — the job is on the server and the next
          // poll (or the socket) will say. Blanking the screen here would be a lie about a job
          // that is very probably still running.
        },
      });
  }

  /**
   * One place where a job's state becomes a screen, whichever channel reported it.
   *
   * The branch is on WHETHER THERE IS A DRAFT, not on the status alone. §16.2 rule 2 produces a
   * row that is `failed` and carries a perfectly reviewable schema, and treating that as a dead end
   * would throw away pages the practice has already paid for.
   */
  private applyImport(dto: CustomFormImportDto): void {
    if (!dto) return;
    this.currentImport = dto;

    if (dto.status !== 'completed' && dto.status !== 'failed') {
      this.phase = 'waiting';
      return;
    }

    this.stopWatching();
    this.schemaErrors = CustomFormService.importSchemaErrors(dto);

    const schema = dto.schema || null;
    const hasQuestions = !!schema && Array.isArray(schema.sections)
      && schema.sections.some((section) => (section.questions || []).length > 0);

    if (hasQuestions) {
      this.extractedSchema = schema;
      this.needsRepair = dto.status === 'failed';
      // The stored reason is kept even here: on a repairable draft it is the list of what
      // `validateSchema` refused, and it is shown above the review button, not instead of it.
      this.importError = dto.status === 'failed' ? dto.errorMessage : null;
      this.phase = 'completed';
      return;
    }

    this.phase = 'failed';
    this.needsRepair = false;

    if (dto.status === 'completed') {
      // "Completed" with nothing in it is a failure however the row is labelled. Saying "ready"
      // here and opening an empty builder would be the most confusing outcome available.
      this.importError = 'The document was read but no questions could be made out of it. '
        + 'A scan of a photograph, or a page of prose with no questions on it, will do this.';
      return;
    }

    // The server's stored reason, verbatim (§16.2 rule 6 stores one for every failure — including
    // an enqueue that never ran). A generic "something went wrong" would hide, for instance,
    // "the document is password protected", which the practice can act on.
    this.importError = dto.errorMessage
      || 'The document could not be read, and the server did not say why.';
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The two ways out
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * The primary action on a finished import, and the ONLY thing it does is open the draft in the
   * builder. Publishing stays where it has always been: a separate, explicit press, by a person who
   * has read the questions.
   */
  review(): void {
    if (!this.extractedSchema || !this.currentImport) return;
    this.dialogRef.close({
      action: 'review',
      importId: this.currentImport.id,
      schema: this.extractedSchema,
      sourceFileName: this.currentImport.sourceFileName,
      sourceFileUrl: this.currentImport.sourceFileUrl,
      pageCount: this.currentImport.pageCount,
      schemaErrors: this.schemaErrors,
    });
  }

  /** Close without taking the draft. It is on the server; the history panel is where it waits. */
  close(): void {
    this.dialogRef.close(undefined);
  }

  /**
   * Back to the picker after a failure.
   *
   * The failed job is NOT forgotten — it is a row on the server and it stays on the form's import
   * history, which is the point of §16.1. This only clears the screen.
   */
  startOver(): void {
    this.stopWatching();
    this.phase = 'choose';
    this.file = null;
    this.filePageCount = null;
    this.fileError = null;
    this.currentImport = null;
    this.importError = null;
    this.extractedSchema = null;
    this.schemaErrors = [];
    this.needsRepair = false;
    this.elapsedSeconds = 0;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Reading the file
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  private looksLikePdfByName(file: File): boolean {
    const type = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    // Some browsers report an empty type for a file dragged from a network share, so the extension
    // is accepted on its own — the header check below is what actually decides.
    return type === 'application/pdf' || (type === '' && name.endsWith('.pdf'))
      || (name.endsWith('.pdf') && type.indexOf('pdf') !== -1);
  }

  /** `%PDF-`, the first five bytes of every PDF. */
  static hasPdfHeader(bytes: Uint8Array): boolean {
    if (!bytes || bytes.length < 5) return false;
    return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46
      && bytes[4] === 0x2d;
  }

  /**
   * BEST-EFFORT page count, and best-effort is the point.
   *
   * A page object is `/Type /Page`, and the page-tree root carries `/Count n`. Neither is visible
   * when the document uses cross-reference streams and puts its page tree inside a compressed
   * object stream — which is why this returns `null` rather than `0` when it finds nothing, and why
   * `null` never refuses an upload. Undercounting costs a server-side refusal the practice would
   * have got anyway; overcounting would refuse a document that is perfectly fine, so the count that
   * decides is the one that can only be too LOW: the explicit page objects, with the page tree's
   * own `/Count` used only when there are no visible page objects at all.
   */
  static readPageCount(bytes: Uint8Array): number | null {
    let text: string;
    try {
      text = new TextDecoder('latin1').decode(bytes);
    } catch {
      return null;
    }

    // `/Type /Page` but not `/Type /Pages` — the tree node is not a page.
    const pageObjects = text.match(/\/Type\s*\/Page(?![a-zA-Z])/g);
    if (pageObjects && pageObjects.length > 0) return pageObjects.length;

    const counts = text.match(/\/Count\s+(\d+)/g);
    if (counts && counts.length > 0) {
      let largest = 0;
      for (const entry of counts) {
        const value = Number(entry.replace(/\/Count\s+/, ''));
        if (Number.isFinite(value) && value > largest) largest = value;
      }
      if (largest > 0) return largest;
    }

    return null;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Small helpers
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  get isBusy(): boolean {
    return this.phase === 'uploading' || this.phase === 'waiting';
  }

  /** Section and question counts, so the completion screen says what actually came back. */
  get extractedSectionCount(): number {
    return this.extractedSchema ? this.extractedSchema.sections.length : 0;
  }

  get extractedQuestionCount(): number {
    if (!this.extractedSchema) return 0;
    let total = 0;
    for (const section of this.extractedSchema.sections) {
      total += (section.questions || []).length;
    }
    return total;
  }

  megabytes(bytes: number): string {
    return (bytes / (1024 * 1024)).toFixed(1);
  }

  /**
   * A server refusal that names its own cap wins over this file's constant.
   *
   * The two numbers live in two repositories with no shared source (§16.3 publishes none), so the
   * screen quotes whichever one it has most recently heard from the server rather than confidently
   * repeating a stale constant back at a practice that has just been refused.
   */
  private applyServerCap(error: any): void {
    const api = CustomFormService.asApiError(error);
    const cap = api && api.details ? api.details['maxPages'] : undefined;
    const asNumber = Number(cap);
    if (Number.isFinite(asNumber) && asNumber > 0) this.maxPages = asNumber;
  }

  private messageFor(error: any, fallback: string): string {
    const api = CustomFormService.asApiError(error);
    return api && api.message ? api.message : fallback;
  }
}
